/**
 * The public Players list: one row per person, and one row per team entry.
 *
 * Pure. The server builds the rows (`buildEventPlayers`, via `loadEventPlayers`,
 * which applies the organiser's switch first); the client only filters them.
 *
 * Person row: the participant ladder without its team rung (an alias holder is
 * called by the alias, and the real name is not sent), initials, photo, profile
 * username (null for a guest), country (only when `nationality` names one),
 * state (no source yet, always null), divisions, rating (null when the event
 * shows none). One row per player across every individual division.
 *
 * Team row (a registration in an `is_team` division with a team name or a
 * roster): titled through the same ladder with its team rung (`team_name`);
 * `members` are the lead's alias-or-name followed by the `team_info` lines as
 * typed, minus a line that is exactly the lead's own first+last name. For an
 * alias-holding lead, a team name or roster line that mentions their real first
 * or last name is dropped too (fail-closed). With no team name the names are
 * the title. No photo, country or rating: they would
 * describe the lead only. The same team in two team divisions is one row.
 */
import type { PublicEntrantRow } from '@/models/eventModel';
import { foldText } from '@/app/[locale]/admin/event-management/[eventId]/edit/libs/tableFilter';
import { resolveParticipantInitials, resolveParticipantLabel } from './participantLabel';
import { resolvePlayerCountry, type PlayerCountry } from './playerCountry';
import { resolveTeamEntry, rosterLines, type TeamEntry } from './teamLabels';

export type EventPlayerDivision = { id: string; name: string; order: number };

export type EventPlayer = {
  /** The player id for a person row; `team:{leadPlayerId}:{n}` for a team row. */
  id: string;
  /** Stable registered player/lead UUID, shared across division-specific team rows. */
  playerId: string;
  kind: 'player' | 'team';
  /** The row title: a person's alias or name; a team's name, or its player names when it has none. */
  label: string;
  /** Team row titled by a team name: the player names under it. Otherwise empty. */
  members: string[];
  initials: string;
  /** Person rows only. */
  photo: string | null;
  /** `/{locale}/user/{username}`: the person's own, or a team's lead (plan D7); null for a guest. */
  profileUsername: string | null;
  /** A guest account (no public profile). Person rows carry the account's flag; team rows are false. */
  isGuest?: boolean;
  /** Person rows only. */
  country: PlayerCountry | null;
  /** No table holds a player's state yet (plan D1); always null for now. */
  state: string | null;
  divisions: EventPlayerDivision[];
  /** Person rows only. Rounded; null when unrated or when the event shows no ratings. */
  rating: number | null;
};

type EntrantPlayer = NonNullable<PublicEntrantRow['player']>;

/** Fold for search: case, accents, and any run of whitespace (incl. U+3000) → one space. */
const fold = (s: string) => foldText(s).replace(/\s+/g, ' ');
const compact = (s: string) => foldText(s).replace(/\s+/g, '');

/** A roster line that is just the lead's own name, in either order. */
function isOwnName(line: string, p: EntrantPlayer): boolean {
  const l = compact(line);
  return (
    l !== '' &&
    (l === compact(p.first_name + p.last_name) || l === compact(p.last_name + p.first_name))
  );
}

/** True when the line contains the player's real first or last name (an alias holder's real name is never sent). */
function mentionsRealName(line: string, p: EntrantPlayer): boolean {
  const l = fold(line);
  const first = fold(p.first_name);
  const last = fold(p.last_name);
  return (first !== '' && l.includes(first)) || (last !== '' && l.includes(last));
}

const profileOf = (p: EntrantPlayer) => (!p.is_guest && p.username ? p.username : null);

function teamRow(
  player: EntrantPlayer,
  rawEntry: TeamEntry,
  isJapanese: boolean,
  n: number,
): EventPlayer {
  // An alias holder's real name is never sent (D5), so a team name that mentions their real
  // first or last name ("Kenji Lin / Amy Wu", "Team Lin") is treated as no team name — the
  // same fail-closed rule as the roster lines below.
  const hidesRealName = !!(player.alias ?? '').trim();
  const entry: TeamEntry =
    hidesRealName && rawEntry.name && mentionsRealName(rawEntry.name, player)
      ? { ...rawEntry, name: null }
      : rawEntry;
  const lead = resolveParticipantLabel({ player, isJapanese });
  const names = [
    lead,
    ...rosterLines(entry.roster).filter(
      (line) => !isOwnName(line, player) && !(hidesRealName && mentionsRealName(line, player)),
    ),
  ].filter(Boolean);
  return {
    id: `team:${player.id}:${n}`,
    playerId: player.id,
    kind: 'team',
    label: entry.name ? resolveParticipantLabel({ entry, player, isJapanese }) : names.join(' · '),
    members: entry.name ? names : [],
    initials: resolveParticipantInitials({ entry, player }),
    photo: null,
    profileUsername: profileOf(player),
    isGuest: false,
    country: null,
    state: null,
    divisions: [],
    rating: null,
  };
}

export function buildEventPlayers(
  rows: readonly PublicEntrantRow[],
  opts: { isJapanese: boolean; showRatings: boolean; locale: string },
): EventPlayer[] {
  const people = new Map<
    string,
    { player: EntrantPlayer; divisions: Map<string, EventPlayerDivision> }
  >();
  const teams = new Map<
    string,
    { row: EventPlayer; divisions: Map<string, EventPlayerDivision> }
  >();

  for (const r of rows) {
    const { player, event_group: group } = r;
    if (!player || !group) continue;
    const division = {
      id: group.id,
      name: group.name,
      order: group.sort_order ?? Number.MAX_SAFE_INTEGER,
    };
    const entry = group.is_team
      ? resolveTeamEntry({
          player_id: player.id,
          team_name: r.team_name,
          team_info: r.team_info,
          player,
        })
      : null;

    if (!entry) {
      const person = people.get(player.id) ?? { player, divisions: new Map() };
      person.divisions.set(group.id, division);
      people.set(player.id, person);
      continue;
    }

    const key = [player.id, entry.name ?? '', entry.roster ?? ''].join('\u0000');
    let team = teams.get(key);
    if (!team) {
      team = { row: teamRow(player, entry, opts.isJapanese, teams.size), divisions: new Map() };
      teams.set(key, team);
    }
    team.divisions.set(group.id, division);
  }

  const collator = new Intl.Collator(opts.locale, { sensitivity: 'base', numeric: true });
  const byOrder = (a: EventPlayerDivision, b: EventPlayerDivision) =>
    a.order - b.order || collator.compare(a.name, b.name);

  const personRows = [...people.values()].map(
    ({ player, divisions }): EventPlayer => ({
      id: player.id,
      playerId: player.id,
      kind: 'player',
      label: resolveParticipantLabel({ player, isJapanese: opts.isJapanese }),
      members: [],
      initials: resolveParticipantInitials({ player }),
      photo: player.profile_photo || null,
      profileUsername: profileOf(player),
      isGuest: !!player.is_guest,
      country: resolvePlayerCountry(player.nationality),
      state: null,
      divisions: [...divisions.values()].sort(byOrder),
      rating:
        opts.showRatings && player.current_rating != null
          ? Math.round(player.current_rating)
          : null,
    }),
  );
  const teamRows = [...teams.values()].map(({ row, divisions }) => ({
    ...row,
    divisions: [...divisions.values()].sort(byOrder),
  }));

  return [...personRows, ...teamRows].sort(
    (a, b) => collator.compare(a.label, b.label) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}

export type PlayerFilter = { query: string; country: string; state: string; division: string };

export const NO_PLAYER_FILTER: PlayerFilter = { query: '', country: '', state: '', division: '' };

export function hasPlayerFilter(f: PlayerFilter): boolean {
  return fold(f.query) !== '' || !!f.country || !!f.state || !!f.division;
}

export function filterEventPlayers(
  players: readonly EventPlayer[],
  f: PlayerFilter,
): EventPlayer[] {
  const q = fold(f.query);
  return players.filter(
    (p) =>
      (!q || [p.label, ...p.members].some((name) => fold(name).includes(q))) &&
      (!f.country || p.country?.code === f.country) &&
      (!f.state || p.state === f.state) &&
      (!f.division || p.divisions.some((d) => d.id === f.division)),
  );
}

export type PlayerFacets = {
  countries: Array<PlayerCountry & { count: number }>;
  states: Array<{ value: string; count: number }>;
  divisions: Array<EventPlayerDivision & { count: number }>;
};

export function playerFacets(players: readonly EventPlayer[]): PlayerFacets {
  const countries = new Map<string, PlayerCountry & { count: number }>();
  const states = new Map<string, number>();
  const divisions = new Map<string, EventPlayerDivision & { count: number }>();
  for (const p of players) {
    if (p.country) {
      const c = countries.get(p.country.code) ?? { ...p.country, count: 0 };
      c.count += 1;
      countries.set(p.country.code, c);
    }
    if (p.state) states.set(p.state, (states.get(p.state) ?? 0) + 1);
    for (const d of p.divisions) {
      const entry = divisions.get(d.id) ?? { ...d, count: 0 };
      entry.count += 1;
      divisions.set(d.id, entry);
    }
  }
  return {
    countries: [...countries.values()].sort((a, b) => a.iso3.localeCompare(b.iso3)),
    states: [...states.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => a.value.localeCompare(b.value)),
    divisions: [...divisions.values()].sort(
      (a, b) => a.order - b.order || a.name.localeCompare(b.name),
    ),
  };
}

/** Caller-owned facet counts apply search and the other facets, never their own selection. */
export function playerFacetCounts(players: readonly EventPlayer[], filter: PlayerFilter) {
  const count = (candidates: EventPlayer[], key: 'country' | 'state' | 'division') => {
    const facets = playerFacets(candidates);
    const options: Record<string, number> =
      key === 'country'
        ? Object.fromEntries(facets.countries.map((c) => [c.code, c.count]))
        : key === 'state'
          ? Object.fromEntries(facets.states.map((s) => [s.value, s.count]))
          : Object.fromEntries(facets.divisions.map((d) => [d.id, d.count]));
    return { all: candidates.length, options };
  };
  return {
    country: count(filterEventPlayers(players, { ...filter, country: '' }), 'country'),
    state: count(filterEventPlayers(players, { ...filter, state: '' }), 'state'),
    division: count(filterEventPlayers(players, { ...filter, division: '' }), 'division'),
  };
}

/** "AUS · NSW", "AUS", or null when no country is known (the row then shows its divisions). */
export function playerPlaceText(p: Pick<EventPlayer, 'country' | 'state'>): string | null {
  if (!p.country) return null;
  return p.state ? `${p.country.iso3} · ${p.state}` : p.country.iso3;
}

/** True once any team row is listed: the count then says "entries" (plan D8). */
export function hasTeamRows(players: readonly EventPlayer[]): boolean {
  return players.some((p) => p.kind === 'team');
}

export type EventPlayerFace = Pick<EventPlayer, 'id' | 'label' | 'initials' | 'photo'>;
export type EventPlayersSummary = { count: number; teams: boolean; faces: EventPlayerFace[] };

/** Compact entrant summary: row count, team presence and a few faces (photos first, then A–Z). */
export function summariseEventPlayers(
  players: readonly EventPlayer[],
  faces = 4,
): EventPlayersSummary {
  const withPhoto = players.filter((p) => p.photo);
  const without = players.filter((p) => !p.photo);
  return {
    count: players.length,
    teams: hasTeamRows(players),
    faces: [...withPhoto, ...without]
      .slice(0, faces)
      .map(({ id, label, initials, photo }) => ({ id, label, initials, photo })),
  };
}

/** Locale-independent canonical event identity; callers prepend their locale. */
export function eventPlayerHref(
  eventId: string,
  slug: string,
  playerId: string,
  division?: string,
): string {
  const path = `/event/${encodeURIComponent(eventId)}/${encodeURIComponent(slug)}/players/${encodeURIComponent(playerId)}`;
  return division ? `${path}?${new URLSearchParams({ division })}` : path;
}

/** A division's public draw page, relative to the locale (prefix `/${locale}`). */
export function eventDrawHref(eventId: string, slug: string, drawId: string): string {
  return `/event/${encodeURIComponent(eventId)}/${encodeURIComponent(slug)}/draw/${encodeURIComponent(drawId)}`;
}
